import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { LucideIcon } from "../FG/LucideIcon";
import "../Planner/planner.css";
import { useDevice } from "../../context/DeviceProvider";
import { useVerticalScrollHints } from "../../hooks/useVerticalScrollHints";

/**
 * Reusable modal chrome — head with eyebrow + title + close button, scrollable
 * body, optional footer. Styled to match design-handoff `modal` / `modal.wide`.
 * Rendered inside the global `<ModalProvider>` portal.
 */
export function ModalShell({
  eyebrow,
  title,
  subtitle,
  headerContent,
  onClose,
  onBack,
  children,
  footer,
  overlay,
  footerSpread = false,
  progress,
  width = 720,
  /** Mobile bottom sheet: use most of the viewport (wizard race step, etc.). */
  tallSheet = false,
  scrollResetKey
}: {
  /** Eyebrow above the title — a plain string or a composed node (icon + step counter). */
  eyebrow?: ReactNode;
  title: string;
  /** Muted lede under the title (design `mh-sub`). */
  subtitle?: string | null;
  headerContent?: ReactNode;
  onClose: () => void;
  /** Circular back control left of the title (sub-modals returning to a hub). */
  onBack?: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Inline scrim/dialog layered over the full shell (header, body, footer). */
  overlay?: ReactNode;
  /** Space-between footer layout (edit workout delete left, actions right). */
  footerSpread?: boolean;
  /** 0..1 — renders the 4px wizard progress bar along the modal's top edge. */
  progress?: number;
  width?: number;
  tallSheet?: boolean;
  /** When this value changes, the scrollable body resets to the top (wizard steps, pref sub-views). */
  scrollResetKey?: string | number;
}) {
  const device = useDevice();
  const isMobile = device === "mobile";
  const shellWidth = isMobile ? "100%" : `min(${width}px, calc(100vw - 48px))`;
  const bodyRef = useRef<HTMLDivElement>(null);
  const scrollHints = useVerticalScrollHints(bodyRef);

  useEffect(() => {
    if (scrollResetKey === undefined) return;
    const el = bodyRef.current;
    if (!el) return;
    const reset = () => {
      el.scrollTop = 0;
      el.scrollTo?.({ top: 0, left: 0, behavior: "instant" });
    };
    reset();
    const raf = requestAnimationFrame(reset);
    return () => cancelAnimationFrame(raf);
  }, [scrollResetKey]);
  /** Sub-modals: eyebrow stays in the chrome row with ← / ✕; title lede drops into the body pad. */
  const titleInBody = !!onBack && !headerContent;

  return (
    <div
      role="document"
      className={`fgp-modalshell${isMobile ? " fgp-modalshell--sheet" : ""}${
        isMobile && tallSheet ? " fgp-modalshell--sheet-tall" : ""
      }`}
      style={{ width: shellWidth }}
      onClick={(e) => e.stopPropagation()}>
      {progress !== undefined && (
        <div className="fgp-modalshell__progress" aria-hidden>
          <span style={{ width: `${Math.max(0, Math.min(1, progress)) * 100}%` }} />
        </div>
      )}
      {isMobile && (
        <div className="fgp-modalshell__grab" aria-hidden>
          <span />
        </div>
      )}
      <div
        className={
          "fgp-modalshell__head" +
          (isMobile ? " fgp-modalshell__head--mobile" : "") +
          (onBack ? " fgp-modalshell__head--back" : "")
        }>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            aria-label="Back"
            title="Back"
            className="fgp-modalshell__back">
            <LucideIcon name="chevron-left" size={18} />
          </button>
        )}
        {headerContent ? (
          <div className="fgp-modalshell__headmain">{headerContent}</div>
        ) : titleInBody ? (
          <div className="fgp-modalshell__headmain fgp-modalshell__headmain--back">
            {eyebrow && <div className="fg-eyebrow fgp-modalshell__eyebrow">{eyebrow}</div>}
          </div>
        ) : (
          <div className="fgp-modalshell__headmain">
            {eyebrow && <div className="fg-eyebrow fgp-modalshell__eyebrow">{eyebrow}</div>}
            <h2 data-fg-modal-title="" className="fgp-modalshell__title">
              {title}
            </h2>
            {subtitle && <div className="fgp-modalshell__subtitle">{subtitle}</div>}
          </div>
        )}
        <button type="button" onClick={onClose} aria-label="Close" className="fgp-modalshell__close">
          <LucideIcon name="x" size={18} />
        </button>
      </div>
      <div
        className={
          "fgp-modalshell__body-wrap" +
          (scrollHints.bottom ? " fgp-modalshell__body-wrap--scroll-bottom" : "") +
          (scrollHints.top ? " fgp-modalshell__body-wrap--scroll-top" : "")
        }>
        <div ref={bodyRef} className="fgp-modalshell__body">
          {titleInBody && (
            <div className="fgp-modalshell__intro">
              <h2 data-fg-modal-title="" className="fgp-modalshell__title">
                {title}
              </h2>
              {subtitle && <div className="fgp-modalshell__subtitle">{subtitle}</div>}
            </div>
          )}
          {children}
        </div>
        <span className="fgp-modalshell__scroll-edge fgp-modalshell__scroll-edge--bottom" aria-hidden>
          <LucideIcon name="chevron-down" size={16} className="fgp-modalshell__scroll-edge-chevron" />
        </span>
        <span className="fgp-modalshell__scroll-edge fgp-modalshell__scroll-edge--top" aria-hidden>
          <LucideIcon name="chevron-up" size={16} className="fgp-modalshell__scroll-edge-chevron" />
        </span>
      </div>
      {footer && (
        <div
          className={
            "fgp-modalshell__foot" +
            (isMobile ? " fgp-modalshell__foot--mobile" : "") +
            (footerSpread ? " fgp-modalshell__foot--spread" : "")
          }>
          {footer}
        </div>
      )}
      {overlay}
    </div>
  );
}
